import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { CamionMuelle, CargaSalida, SlotMuelle } from '@/types'
import type { DockTrack } from '@/utils/dockTimeTracks'
import { CarrilTrack } from '@/components/planning/CarrilTrack'
import { repartirCarriles } from '@/utils/dockCarriles'
import { tramoDe } from '@/utils/camionesMuelle'
import { ocupacionDe } from '@/utils/dockOcupacion'
import { CARRILES_MAX, INFO_COL_DOCK, altoBarra, altoFila, topCarril } from '@/config/dockSchedule'
import { BarraCamion } from '@/components/muelles/BarraCamion'

/** Lo que viaja en el arrastre. Un tipo propio de MIME para que soltar texto de
 *  fuera del navegador no se confunda con soltar una carga. */
export const MIME_CARGA = 'text/x-carga'

/**
 * La fila de un muelle: panel fijo + carril con N camiones.
 *
 * Es un componente propio y no una extensión de `GanttRow` porque aquélla está
 * atada a una orden de producción, a su semáforo de material y a su subtabla de
 * consumos, y pinta **una** barra. Lo que sí comparten —el cromo del carril— se
 * extrajo a `CarrilTrack`.
 *
 * El panel de la izquierda ordena los tres datos del muelle por lo que se mira
 * primero: el nombre, cuánto está ocupado y cuántos camiones tiene.
 */
export function DockRow({
  slot, camiones, cargasPorNumero, track, dark, onCamion, onHueco, onSoltarCarga,
}: {
  slot: SlotMuelle
  camiones: CamionMuelle[]
  cargasPorNumero: Map<string, CargaSalida>
  track: DockTrack
  dark: boolean
  onCamion?: (c: CamionMuelle) => void
  /** Pinchar un hueco libre del carril: abre el alta con muelle y hora puestos. */
  onHueco?: (slot: SlotMuelle, cuando: Date) => void
  /** Soltar una carga. Sobre un camión, se le engancha; sobre un hueco, se abre
   *  el alta con la carga ya elegida. */
  onSoltarCarga?: (carga: string, destino: { camion?: CamionMuelle; slot: SlotMuelle; cuando: Date }) => void
}) {
  const { t } = useTranslation()
  const [sobrevolando, setSobrevolando] = useState(false)

  const reparto = useMemo(
    () => repartirCarriles(
      camiones.filter((c) => tramoDe(c) !== null),
      (c) => tramoDe(c)!,
      (ini, fin) => track.pos(new Date(ini), new Date(fin)),
      (c) => c.id,
    ),
    [camiones, track],
  )

  const carriles = Math.max(1, Math.min(reparto.carriles, CARRILES_MAX))
  const alto = altoFila(reparto.carriles)
  const altoDeBarra = altoBarra(carriles)

  const ocupacion = useMemo(() => {
    const intervalos = camiones.map(tramoDe).filter((x): x is { iniMs: number; finMs: number } => x !== null)
    return ocupacionDe(
      intervalos,
      track.rangeStart.getTime(),
      track.rangeEnd.getTime(),
      track.horasOperativas,
    )
  }, [camiones, track])

  const bdr = dark ? 'border-white/10' : 'border-slate-200'
  const txtMain = dark ? 'text-slate-100' : 'text-slate-900'
  const txtMuted = dark ? 'text-slate-400' : 'text-slate-500'

  /** Dónde ha caído el puntero, en instante del eje. */
  const instanteDe = (e: React.MouseEvent | React.DragEvent): Date => {
    const caja = e.currentTarget.getBoundingClientRect()
    return track.at(((e.clientX - caja.left) / caja.width) * 100)
  }

  /** Qué camión ocupa ese instante, si es que hay alguno. */
  const camionEn = (cuando: Date): CamionMuelle | undefined => {
    const ms = cuando.getTime()
    return camiones.find((c) => {
      const tr = tramoDe(c)
      return tr && ms >= tr.iniMs && ms < tr.finMs
    })
  }

  return (
    <div
      className={`flex border-b ${bdr} transition-colors ${
        sobrevolando
          ? (dark ? 'bg-sky-950/40' : 'bg-sky-50/70')
          : (dark ? 'bg-slate-900/40' : 'bg-white')
      }`}
      style={{ height: alto }}
    >
      {/* Panel fijo del muelle */}
      <div className={`shrink-0 px-3 py-2 border-r ${bdr} flex flex-col justify-center gap-0.5`} style={{ width: INFO_COL_DOCK }}>
        <div className="flex items-center gap-1.5">
          <span className={`text-[13px] font-bold truncate ${txtMain}`}>{slot.nombre}</span>
          {!slot.enCatalogo && (
            <span
              className={`text-[9px] px-1 rounded ring-1 ring-inset ${
                dark ? 'ring-amber-500/40 text-amber-400' : 'ring-amber-300 text-amber-600'
              }`}
              title={t('muelles.slotFueraDeCatalogoTooltip')}
            >
              {t('muelles.slotFueraDeCatalogo')}
            </span>
          )}
        </div>

        {/* La ocupación, como barra: un número suelto no dice si 5 h es mucho. */}
        <div className="flex items-center gap-1.5">
          <div className={`h-1 flex-1 rounded-full overflow-hidden ${dark ? 'bg-white/10' : 'bg-slate-150 bg-slate-100'}`}>
            <div
              className={`h-full rounded-full ${ocupacion.pct > 85 ? 'bg-rose-500' : 'bg-sky-500'}`}
              style={{ width: `${Math.min(100, ocupacion.pct)}%` }}
            />
          </div>
          <span className={`text-[10px] tabular-nums shrink-0 ${txtMuted}`}>
            {Math.round(ocupacion.pct)}%
          </span>
        </div>

        <div className={`text-[10px] tabular-nums ${txtMuted}`}>
          {camiones.length
            ? t('muelles.nCamiones', { count: camiones.length })
            : t('muelles.libre')}
          {' · '}
          {t('muelles.horasDe', {
            horas: ocupacion.horas.toLocaleString('es-ES', { maximumFractionDigits: 1 }),
            total: Math.round(ocupacion.disponibles),
          })}
        </div>
      </div>

      {/* Carril */}
      <CarrilTrack
        boundaries={track.boundaries}
        todayX={track.todayX}
        dark={dark}
        ahoraDiscontinua={track.ahoraFueraJornada}
        className="flex-1 relative min-w-0 overflow-hidden"
        style={{ height: alto }}
      >
        {/* La superficie que recibe el clic y el arrastre. Va debajo de las
            barras, así que un clic sobre un camión llega al camión. */}
        <div
          className="absolute inset-0"
          onClick={onHueco ? (e) => onHueco(slot, instanteDe(e)) : undefined}
          onDragOver={onSoltarCarga ? (e) => {
            if (!e.dataTransfer.types.includes(MIME_CARGA)) return
            e.preventDefault()
            e.dataTransfer.dropEffect = 'move'
            setSobrevolando(true)
          } : undefined}
          onDragLeave={() => setSobrevolando(false)}
          onDrop={onSoltarCarga ? (e) => {
            setSobrevolando(false)
            const carga = e.dataTransfer.getData(MIME_CARGA)
            if (!carga) return
            e.preventDefault()
            const cuando = instanteDe(e)
            onSoltarCarga(carga, { camion: camionEn(cuando), slot, cuando })
          } : undefined}
          title={onHueco ? t('muelles.reservarAqui') : undefined}
        />

        {reparto.barras.map((b) => (
          <BarraCamion
            key={b.item.id}
            camion={b.item}
            cargas={b.item.cargas
              .map((n) => cargasPorNumero.get(n))
              .filter((c): c is CargaSalida => Boolean(c))}
            left={b.left}
            width={b.width}
            top={topCarril(Math.min(b.carril, CARRILES_MAX - 1), carriles)}
            alto={altoDeBarra}
            minLabelPct={track.minLabelPct}
            arrastrando={sobrevolando}
            onClick={onCamion ? () => onCamion(b.item) : undefined}
          />
        ))}

        {/* A partir del quinto carril se deja de abrir sitio: una fila con ocho
            camiones solapados se comería la pantalla. */}
        {reparto.carriles > CARRILES_MAX && (
          <div
            className={`absolute right-1 top-1 text-[9px] font-bold px-1.5 py-0.5 rounded z-20 ${
              dark ? 'bg-slate-700 text-slate-200' : 'bg-slate-800 text-white'
            }`}
            title={t('muelles.masCamionesTooltip', { n: reparto.carriles - CARRILES_MAX })}
          >
            +{reparto.carriles - CARRILES_MAX}
          </div>
        )}
      </CarrilTrack>
    </div>
  )
}
